import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROLE_LEVELS } from '@genoma/shared';
import type { ParticipantListDto, ParticipantProgressDto, RoleLevel } from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { fetchParticipants, sendParticipantAccess } from '../../services/dashboard.service.ts';
import { useCan } from '../../stores/auth.store.ts';

/**
 * Everybody being evaluated in the cycle.
 *
 * Search and the area filter run against the list already in hand: a cohort is
 * a couple of dozen people, so a round trip per keystroke would add latency and
 * buy nothing. The API supports the same filters for when a cohort is large
 * enough to need them.
 */
/**
 * The account a leader reads their own report with.
 *
 * Here rather than only on their card, because this is the list an admin works
 * down when a cycle closes — the same place the coaches and P&O sections put
 * it. It is not the questionnaire invitation: that sends a magic link to
 * answer with, this hands over a way to read the result.
 *
 * Sending again mints a new password, so for somebody who has already signed
 * in it is a reset, and the button says so before doing it.
 */
const ReportAccess = ({
  participant,
  onSent,
}: {
  participant: ParticipantProgressDto;
  onSent: () => void;
}) => {
  const { t, locale } = useI18n();
  const canGrant = useCan('staff:manage');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Nothing can be sent to the address the 360 import writes when it has none.
  if (!canGrant || participant.access.emailIsPlaceholder) return null;

  const { invitedAt, lastLoginAt } = participant.access;
  const resend = invitedAt !== null;

  const send = async () => {
    setBusy(true);
    try {
      await sendParticipantAccess(participant.personId);
      setConfirming(false);
      onSent();
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="relative z-10 flex shrink-0 items-center gap-2">
      <span className="font-mono text-[9.5px] tracking-[0.08em] text-faint uppercase">
        {lastLoginAt
          ? t('coaches.hasEntered')
          : invitedAt
            ? t('coaches.invitedOn', { date: new Date(invitedAt).toLocaleDateString(locale) })
            : t('participants.noAccessYet')}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={(event) => {
          event.preventDefault();
          if (confirming || !resend) void send();
          else setConfirming(true);
        }}
        className={`cursor-pointer rounded-xl px-3 py-1.5 text-[12px] font-semibold transition disabled:opacity-40 ${
          confirming
            ? 'bg-[var(--color-critical)] text-white'
            : resend
              ? 'border border-line bg-paper text-soft hover:border-teal hover:text-teal'
              : 'bg-teal text-white shadow-action hover:bg-teal-deep'
        }`}
      >
        {busy
          ? t('common.saving')
          : confirming
            ? t('coaches.resendConfirm')
            : resend
              ? t('coaches.resend')
              : t('invitations.sendReportAccess')}
      </button>
    </span>
  );
};

export const ParticipantsPage = () => {
  const { t } = useI18n();
  const [data, setData] = useState<ParticipantListDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [area, setArea] = useState('');
  const [level, setLevel] = useState<RoleLevel | ''>('');

  const load = useCallback(() => {
    fetchParticipants()
      .then(setData)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.participants.filter((participant) => {
      if (area && participant.area !== area) return false;
      if (level && participant.roleLevel !== level) return false;
      if (!needle) return true;
      return [participant.fullName, participant.jobTitle, participant.area]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(needle));
    });
  }, [data, search, area, level]);

  if (error) return <Centered>{error}</Centered>;
  if (!data) return <Centered>{t('common.loading')}</Centered>;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">
        {t('participants.title')}
      </h1>
      <p className="mt-1 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
        {t('participants.count', { shown: visible.length, total: data.total })}
      </p>

      {/* Filters in one row above the list, never scattered beside it. */}
      <div className="mt-5 flex flex-wrap items-center gap-2.5">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('participants.searchPlaceholder')}
          className="min-w-[220px] flex-1 rounded-xl border border-line bg-paper px-4 py-2.5 text-[14px] outline-none focus:border-teal"
        />
        <select
          value={area}
          onChange={(event) => setArea(event.target.value)}
          className="cursor-pointer rounded-xl border border-line bg-paper px-3 py-2.5 text-[14px] outline-none focus:border-teal"
        >
          <option value="">{t('participants.allAreas')}</option>
          {data.areas.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {/* The three levels come from the framework rather than from the
            people on screen: "no Area Leaders in this cycle" is an answer
            worth being able to ask for, and a level added to the ladder
            appears here without anybody remembering this file. */}
        <select
          value={level}
          onChange={(event) => setLevel(event.target.value as RoleLevel | '')}
          className="cursor-pointer rounded-xl border border-line bg-paper px-3 py-2.5 text-[14px] outline-none focus:border-teal"
        >
          <option value="">{t('participants.allLevels')}</option>
          {ROLE_LEVELS.map((option) => (
            <option key={option} value={option}>
              {t(`roleLevels.${option}`)}
            </option>
          ))}
        </select>
      </div>

      {visible.length === 0 ? (
        <p className="mt-8 text-sm text-soft">{t('participants.noMatches')}</p>
      ) : (
        <ul className="mt-5 list-none overflow-hidden rounded-2xl border border-line bg-paper shadow-card">
          {visible.map((participant) => (
            <li
              key={participant.participantId}
              className="relative flex flex-wrap items-center gap-4 border-t border-line-soft px-4 py-3 transition first:border-t-0 hover:bg-tint-soft"
            >
              <span className="min-w-[200px] flex-1">
                {/* One real link, stretched over the row, so the row is a hit
                    target without swallowing the button beside it. */}
                <Link
                  to={`/participantes/${participant.participantId}`}
                  className="block text-[14px] font-bold after:absolute after:inset-0 after:content-['']"
                >
                  {participant.fullName}
                </Link>
                {/* The role level decides which anchors this person was rated
                    against, so two people with similar job titles can be
                    reading different scales. Same line as the detail page, so
                    a row and the page it opens agree. */}
                <span className="block text-[12.5px] text-soft">
                  {participant.jobTitle ?? t(`roleLevels.${participant.roleLevel}`)}
                  {participant.jobTitle && ` · ${t(`roleLevels.${participant.roleLevel}`)}`}
                </span>
              </span>

              {participant.area && (
                <span className="rounded-full border border-line bg-sand px-2.5 py-0.5 font-mono text-[10px] tracking-[0.08em] text-soft uppercase">
                  {participant.area}
                </span>
              )}

              <span className="flex w-32 items-center gap-2">
                <span className="h-1.5 flex-1 rounded-full bg-line-soft">
                  <span
                    className="block h-full rounded-full bg-teal"
                    style={{ width: `${participant.participationRate}%` }}
                  />
                </span>
                <span className="font-mono text-[11.5px] tabular-nums text-soft">
                  {participant.participationRate}%
                </span>
              </span>

              <ReportAccess participant={participant} onSent={load} />
            </li>
          ))}
        </ul>
      )}

      <p className="mt-8 border-t border-line-soft pt-4 text-xs text-faint">
        {t('dashboard.privacyFooter')}
      </p>
    </div>
  );
};
